import { EmptyStateComponent } from 'src/app/Shared/Components/empty-state/empty-state.component';import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { MasterDataService } from 'src/app/Services/master-data.service';
import { ArticleSizeDTO } from 'src/app/DTOs/ArticleSizeDTO';
import { trigger, transition, style, animate } from '@angular/animations';

import { tileRangeLabel } from 'src/app/helper/TileRangeLabel';
import { NotificationService } from 'src/app/Shared/prime-ng/notification.service';
import { DataResult, DataStateChangeEvent, GridDataResult, PageChangeEvent, State, pageSkip, pageTake, process } from 'src/app/Shared/prime-ng/grid-data';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

@Component({
  selector: 'app-article-size-overview',
  standalone: true,
  imports: [PrimeNgModule, CommonModule, RouterModule, FormsModule, EmptyStateComponent],
  templateUrl: './article-size-overview.component.html',
  styleUrl: './article-size-overview.component.css',
  animations: [
    trigger('cardAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class ArticleSizeOverviewComponent implements OnInit, OnDestroy {
  public state: State = {
    skip: 0,
    take: 12,
    sort: [{ field: 'name', dir: 'asc' }]
  };

  public gridView: GridDataResult = { data: [], total: 0 };
  public allRows: ArticleSizeDTO[] = [];
  public loading = false;
  public deleteDialog = false;
  public editDialog = false;
  public selectedArticleSize: ArticleSizeDTO | null = null;
  public searchText = '';
  public activeActionMenuKey: string | null = null;
  get footerRangeLabel(): string {
    return tileRangeLabel(
      this.gridView.total,
      this.state.take ?? 12,
      this.state.skip ?? 0,
    );
  }

  private destroy$ = new Subject<void>();

  constructor(
    private masterData: MasterDataService,
    private notificationService: NotificationService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadArticleSizes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadArticleSizes(): void {
    this.loading = true;
    this.masterData.getArticleSizes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.allRows = rows ?? [];
          this.applyFilters();
          this.loading = false;
        },
        error: () => {
          this.allRows = [];
          this.applyFilters();
          this.loading = false;
          this.notification('Error loading Article Sizes', 'error');
        }
      });
  }

  public onSearchChange(): void {
    this.state.skip = 0;
    this.applyFilters();
  }

  public clearSearch(): void {
    this.searchText = '';
    this.state.skip = 0;
    this.applyFilters();
  }

  private applyFilters(): void {
    let filtered = [...this.allRows];

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(search)
      );
    }

    this.gridView = process(filtered, this.state);
  }

  public pageChange(event: PageChangeEvent): void {
    this.state.skip = pageSkip(event);
    this.state.take = pageTake(event);
    this.applyFilters();
  }
  public toggleActionMenu(key: string | number, event: MouseEvent): void {    event.stopPropagation();    const menuKey = String(key);    this.activeActionMenuKey = this.activeActionMenuKey === menuKey ? null : menuKey;  }  public isActionMenuOpen(key: string | number): boolean {    return this.activeActionMenuKey === String(key);  }  public closeActionMenu(): void {    this.activeActionMenuKey = null;  }
  public createArticleSize(): void {
    this.selectedArticleSize = {
      databaseId: 0,
      name: '',
      isInternalSize: false,
      isMasterData: false,
      canBeDeleted: true,
    };
    this.editDialog = true;
  }

  public editArticleSize(articleSize: ArticleSizeDTO): void {
    this.selectedArticleSize = { ...articleSize };
    this.editDialog = true;
  }

  public saveArticleSize(): void {
    if (!this.selectedArticleSize) return;

    if (!this.selectedArticleSize.name || this.selectedArticleSize.name.trim() === '') {
      this.notification('Please enter a name', 'warning');
      return;
    }

    const articleSize = this.selectedArticleSize;

    if (articleSize.databaseId === 0) {
      this.masterData.createArticleSize(articleSize)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (createdId) => {
            articleSize.databaseId = createdId;
            this.allRows.unshift(articleSize);
            this.applyFilters();
            this.notification(`Article Size "${articleSize.name}" created`, 'success');
            this.toggleEditDialog();
          },
          error: () => {
            this.notification('Error creating Article Size', 'error');
          }
        });
    } else {
      this.masterData.updateArticleSize(articleSize)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            const index = this.allRows.findIndex(a => a.databaseId === articleSize.databaseId);
            if (index > -1) {
              this.allRows[index] = articleSize;
            }
            this.applyFilters();
            this.notification(`Article Size "${articleSize.name}" updated`, 'success');
            this.toggleEditDialog();
          },
          error: () => {
            this.notification('Error updating Article Size', 'error');
          }
        });
    }
  }

  public deleteArticleSize(articleSize: ArticleSizeDTO): void {
    this.selectedArticleSize = articleSize;
    this.deleteDialog = true;
  }

  public confirmDelete(): void {
    if (!this.selectedArticleSize) return;

    const articleSize = this.selectedArticleSize;

    this.masterData.deleteArticleSize(articleSize.databaseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.allRows = this.allRows.filter(a => a.databaseId !== articleSize.databaseId);
          this.applyFilters();
          this.notification(`Article Size "${articleSize.name}" deleted`, 'success');
          this.toggleDeleteDialog();
        },
        error: () => {
          this.notification('Error deleting Article Size', 'error');
          this.toggleDeleteDialog();
        }
      });
  }

  public toggleDeleteDialog(): void {
    this.deleteDialog = !this.deleteDialog;
    if (!this.deleteDialog) {
      this.selectedArticleSize = null;
    }
  }

  public toggleEditDialog(): void {
    this.editDialog = !this.editDialog;
    if (!this.editDialog) {
      this.selectedArticleSize = null;
    }
  }

  private notification(message: string, state: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: state, icon: true }
    });
  }
}
